import type { CSSProperties } from "react";
import {
  imageAdjustmentsToCssFilter,
  layoutImageInBox,
  readImageAdjustments,
  readImageCrop,
  readImageObjectFit,
} from "@archicreation/card-editor-engine/image-adjustments";
import type { InvitationCardBlockDto } from "@/lib/api/types";
import { resolveImageBlockUrl } from "@/services/assets";
import {
  GUEST_CARD_DESIGN_H,
  GUEST_CARD_DESIGN_W,
} from "./guestCardDimensions";
import styles from "./GuestCardSurface.module.scss";

const DEFAULT_GUEST_TEXT_FONT = 'Georgia, "Times New Roman", serif';

type GuestCardSurfaceProps = {
  blocks: InvitationCardBlockDto[];
};

function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function composeTransform(
  rotation: number,
  flipX: boolean,
  flipY: boolean,
): string | undefined {
  const parts: string[] = [];
  if (rotation) parts.push(`rotate(${rotation}deg)`);
  if (flipX) parts.push("scaleX(-1)");
  if (flipY) parts.push("scaleY(-1)");
  return parts.length ? parts.join(" ") : undefined;
}

function BlockView({ block }: { block: InvitationCardBlockDto }) {
  const p = block.props;
  const x = num(p.x);
  const y = num(p.y);
  const rotation = num(p.rotation);

  if (block.type === "text") {
    const width = num(p.width, 200);
    const fontSize = num(p.fontSize, 16);
    return (
      <div
        className={styles.textBlock}
        style={{
          left: x,
          top: y,
          width,
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          fontSize,
          fontFamily: str(p.fontFamily, DEFAULT_GUEST_TEXT_FONT),
          fontWeight: p.bold === true ? 700 : 400,
          fontStyle: p.italic === true ? "italic" : "normal",
          textDecoration: p.underline === true ? "underline" : "none",
          color: str(p.fill, "#3e3426"),
          textAlign: (str(p.align, "left") ||
            "left") as CSSProperties["textAlign"],
          opacity: p.opacity == null ? 1 : num(p.opacity, 1),
        }}
      >
        {str(p.text)}
      </div>
    );
  }

  if (block.type === "rect") {
    return (
      <div
        className={styles.rectBlock}
        style={{
          left: x,
          top: y,
          width: num(p.width, 100),
          height: num(p.height, 100),
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          background: str(p.fill, "#d9d2c5"),
          border:
            p.stroke && num(p.strokeWidth)
              ? `${num(p.strokeWidth)}px solid ${str(p.stroke)}`
              : undefined,
          borderRadius: num(p.cornerRadius),
          opacity: p.opacity == null ? 1 : num(p.opacity, 1),
        }}
      />
    );
  }

  if (block.type === "image") {
    const url = resolveImageBlockUrl(p);
    if (!url) return null;
    const w = num(p.width, 200);
    const h = num(p.height, 200);
    const crop = readImageCrop(p.crop);
    const objectFit = readImageObjectFit(p.objectFit);
    const flipX = p.flipX === true;
    const flipY = p.flipY === true;
    const filter = imageAdjustmentsToCssFilter(
      readImageAdjustments(p.adjustments),
    );
    const nw = num(p.naturalWidth);
    const nh = num(p.naturalHeight);
    const srcW = crop && nw > 0 ? crop.width * nw : nw > 0 ? nw : w;
    const srcH = crop && nh > 0 ? crop.height * nh : nh > 0 ? nh : h;
    const layout = layoutImageInBox(w, h, srcW, srcH, objectFit);
    const needsFrame =
      Boolean(crop) ||
      num(p.cornerRadius) > 0 ||
      objectFit !== "fill" ||
      flipX ||
      flipY;
    const frameStyle: CSSProperties = {
      left: x,
      top: y,
      width: w,
      height: h,
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      opacity: p.opacity == null ? 1 : num(p.opacity, 1),
      borderRadius: num(p.cornerRadius),
      overflow: needsFrame ? "hidden" : undefined,
    };

    const imgStyle: CSSProperties = crop
      ? {
          width: layout.width / crop.width,
          height: layout.height / crop.height,
          left: layout.x - (crop.x / crop.width) * layout.width,
          top: layout.y - (crop.y / crop.height) * layout.height,
          filter: filter === "none" ? undefined : filter,
        }
      : {
          width: layout.width,
          height: layout.height,
          left: layout.x,
          top: layout.y,
          objectFit: "fill",
          filter: filter === "none" ? undefined : filter,
        };

    const flipStyle: CSSProperties | undefined =
      flipX || flipY
        ? {
            width: "100%",
            height: "100%",
            transform: composeTransform(0, flipX, flipY),
            transformOrigin: "center center",
            position: "relative",
          }
        : undefined;

    if (!needsFrame && !crop) {
      return (
        <img
          className={styles.imageBlock}
          src={url}
          alt={str(p.alt, "Invitation image")}
          style={{
            ...frameStyle,
            borderRadius: undefined,
            overflow: undefined,
            objectFit,
            filter: filter === "none" ? undefined : filter,
          }}
        />
      );
    }

    const image = (
      <img
        className={styles.imageCropInner}
        src={url}
        alt={str(p.alt, "Invitation image")}
        style={imgStyle}
      />
    );

    return (
      <div className={styles.imageCropFrame} style={frameStyle}>
        {flipStyle ? <div style={flipStyle}>{image}</div> : image}
      </div>
    );
  }

  return null;
}

/** Read-only HTML card surface in design coordinates (scaled by parent). */
export function GuestCardSurface({ blocks }: GuestCardSurfaceProps) {
  return (
    <div
      className={styles.surface}
      style={{ width: GUEST_CARD_DESIGN_W, height: GUEST_CARD_DESIGN_H }}
      aria-label="Invitation card"
    >
      {blocks.map((b) => (
        <BlockView key={b.id} block={b} />
      ))}
    </div>
  );
}
